import { useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  Card,
  DataList,
  Flex,
  IconButton,
  ScrollArea,
  Text,
  TextField,
} from "@radix-ui/themes";
import "./FeatureFlag.module.scss";
import Switch from "@/components/shared/inputs/switch";
import {
  FeatureFlag,
  featureFlags,
} from "@/v2/components/feature-flag/FeatureFlag.model";
import {
  useFlagOverridesStore,
  useFlag,
} from "@/v2/components/feature-flag/FeatureFlagContext";
import { AiOutlineClose, AiOutlineFlag } from "react-icons/ai";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { TextWithTooltip } from "@/components/shared/tooltip/TextWithTooltip";

function FeatureFlagItem({ flag }: { flag: FeatureFlag }) {
  const configValue = useFlag(flag);

  const override = useFlagOverridesStore((s) => s.overrides[flag]);
  const addOverride = useFlagOverridesStore((s) => s.addOverride);
  const updateOverride = useFlagOverridesStore((s) => s.updateOverride);
  const removeOverride = useFlagOverridesStore((s) => s.removeOverride);

  const [value, setValue] = useState(() =>
    override !== undefined ? override : configValue
  );

  const handleChange = useCallback(
    (newVal: boolean) => {
      setValue(newVal);

      if (override !== undefined) {
        updateOverride(flag, newVal);
      } else {
        addOverride(flag, newVal);
      }
    },
    [flag, override, addOverride, updateOverride]
  );

  const handleRemove = useCallback(() => {
    removeOverride(flag);
  }, [flag, removeOverride]);

  useEffect(() => {
    setValue(override !== undefined ? override : configValue);
  }, [configValue, override]);

  return (
    <DataList.Item align="center">
      <DataList.Label minWidth="160px" maxWidth={"220px"}>
        <TextWithTooltip size={"2"} color={"gray"} weight={"medium"} truncate>
          {flag}
        </TextWithTooltip>
      </DataList.Label>

      <DataList.Value className="flex justify-end items-center gap-2 pr-2">
        {override !== undefined && (
          <IconButton
            size={"1"}
            variant="soft"
            color="gray"
            onClick={handleRemove}
          >
            <AiOutlineClose />
          </IconButton>
        )}

        <Switch value={value} onChange={handleChange} />
      </DataList.Value>
    </DataList.Item>
  );
}

export function FeatureFlagOverrides() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredFlags = useMemo(() => {
    return featureFlags.filter((flag) =>
      flag.toLowerCase().includes(search.toLowerCase())
    );
  }, [search]);

  return (
    <>
      <Button
        onClick={() => setOpen(!open)}
        variant="soft"
        color="blue"
        id="feature-flag-overrides-button"
        className="fixed bottom-4 left-4 z-50"
      >
        <AiOutlineFlag />
        Flags
      </Button>

      {open && (
        <Card className="fixed bottom-4 left-4 z-50 w-[360px] h-[600px]">
          <Flex height={"100%"} direction={"column"} gap={"2"}>
            <Flex justify={"between"} align={"center"} gap={"2"}>
              <Text size={"4"} color={"gray"} weight={"medium"} highContrast>
                Flag Overrides
              </Text>

              <IconButton
                variant="soft"
                color="gray"
                onClick={() => setOpen(false)}
              >
                <AiOutlineClose />
              </IconButton>
            </Flex>

            <TextField.Root
              size="2"
              placeholder={"Search..."}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            >
              <TextField.Slot>
                <MagnifyingGlassIcon height="16" width="16" />
              </TextField.Slot>
            </TextField.Root>

            <Flex
              height={"100%"}
              py={"2"}
              direction={"column"}
              overflow={"hidden"}
            >
              {filteredFlags.length === 0 && (
                <Text size={"2"} color={"gray"}>
                  No flags found
                </Text>
              )}

              {filteredFlags.length > 0 && (
                <ScrollArea scrollbars="vertical">
                  <DataList.Root className="py-2">
                    {filteredFlags.map((flag) => (
                      <FeatureFlagItem key={flag} flag={flag} />
                    ))}
                  </DataList.Root>
                </ScrollArea>
              )}
            </Flex>
          </Flex>
        </Card>
      )}
    </>
  );
}
